import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import {
  FaTruck, FaPlane, FaUser, FaGasPump, FaClock,
  FaCheckCircle, FaBolt, FaMapMarkerAlt, FaArrowRight
} from 'react-icons/fa';
import '../styles/LiveFueling.css';

/* ── Coordinates ────────────────────────────────────────────────── */
const CENTER = { lat: 5.811487489669329, lng: -55.1971283564614 };

/* ── Marker icon URLs ───────────────────────────────────────────── */
const ICON_TANKER_ACTIVE = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
const ICON_TANKER_IDLE   = 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png';
const ICON_AIRCRAFT      = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';

/* ── Map options ────────────────────────────────────────────────── */
const MAP_OPTIONS = {
  mapTypeId:         'satellite',
  tilt:              0,
  zoomControl:       true,
  mapTypeControl:    false,
  streetViewControl: false,
  rotateControl:     false,
  fullscreenControl: true,
  scaleControl:      true,
};

/* ── Polyline (hose line) ───────────────────────────────────────── */
const HOSE_LINE_OPTIONS = {
  strokeColor:   '#db3131',
  strokeOpacity: 0.9,
  strokeWeight:  3,
  icons: [{
    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 },
    offset: '0',
    repeat:  '18px',
  }],
};

/* ── Dummy data ─────────────────────────────────────────────────── */
const INITIAL_SESSIONS = [
  {
    id: 'SES-001',
    tanker: {
      id: 1, name: 'Tanker A', fuelType: 'JET A-1', level: 68,
      lat: 5.81170, lng: -55.19663,
    },
    aircraft: {
      reg: 'PZ-TAB', type: 'Cessna 172', position: 'Apron 1 · Bay 3',
      lat: 5.81190, lng: -55.19640,
    },
    fueler:    'Carlos Hew',
    startTime: Date.now() - 7 * 60 * 1000,
    step: 7, totalSteps: 9, dispensed: 84, status: 'fueling',
  },
  {
    id: 'SES-002',
    tanker: {
      id: 2, name: 'Tanker B', fuelType: 'JET A-1', level: 41,
      lat: 5.81125, lng: -55.19755,
    },
    aircraft: {
      reg: 'PZ-SUR', type: 'Piper PA-28', position: 'Apron 1 · Bay 7',
      lat: 5.81108, lng: -55.19780,
    },
    fueler:    'Mark Lie',
    startTime: Date.now() - 3 * 60 * 1000,
    step: 4, totalSteps: 9, dispensed: 38, status: 'fueling',
  },
];

const IDLE_TANKERS = [
  {
    id: 3, name: 'Tanker C', fuelType: 'AVGAS 100LL', level: 18,
    location: 'Hangar 3', lat: 5.81100, lng: -55.19672,
  },
];

const RECENT_COMPLETED = [
  { id: 'SES-000', tanker: 'Tanker A', aircraft: 'PZ-DBN', fueler: 'Sam Jones', amount: '210 Gal', duration: '14 min', completedAgo: '22 min ago' },
  { id: 'SES-EEE', tanker: 'Tanker C', aircraft: 'PZ-KAS', fueler: 'Roy Brunings', amount: '95 Gal', duration: '9 min', completedAgo: '1 hr ago' },
];

/* ── Helpers ────────────────────────────────────────────────────── */
function elapsed(startTs) {
  const s = Math.floor((Date.now() - startTs) / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
}

function FuelBar({ level, height = 10 }) {
  const color = level < 20 ? '#db3131' : level < 40 ? '#f5af18' : '#35c068';
  return (
    <div className="lf-bar-track" style={{ height }}>
      <div className="lf-bar-fill" style={{ width: `${level}%`, background: color }} />
    </div>
  );
}

function StepProgress({ step, total }) {
  return (
    <div className="lf-steps">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`lf-step-dot ${i < step ? 'done' : i === step ? 'active' : ''}`}
        />
      ))}
    </div>
  );
}

function PulsingDot() {
  return <span className="pulsing-dot" />;
}

/* ── Apron Map ─────────────────────────────────────────────────── */
function ApronMap({ sessions, idleTankers }) {
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const handleLoad = useCallback((map) => { mapRef.current = map; }, []);
  const closeInfo  = useCallback(() => setSelected(null), []);

  // Re-trigger map resize when the browser tab becomes visible again
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && mapRef.current) {
        window.google?.maps?.event?.trigger(mapRef.current, 'resize');
        mapRef.current.setCenter(CENTER);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  if (!isLoaded) {
    return <div className="lf-map-wrapper">Loading map…</div>;
  }

  return (
      <div className="lf-map-wrapper">
        <GoogleMap
          mapContainerClassName="lf-map-container"
          center={CENTER}
          zoom={18}
          options={MAP_OPTIONS}
          onLoad={handleLoad}
        >
          {/* Dashed hose lines: tanker → aircraft */}
          {sessions.map(s => (
            <Polyline
              key={`line-${s.id}`}
              path={[
                { lat: s.tanker.lat,   lng: s.tanker.lng },
                { lat: s.aircraft.lat, lng: s.aircraft.lng },
              ]}
              options={HOSE_LINE_OPTIONS}
            />
          ))}

          {/* Active tanker markers (red) */}
          {sessions.map(s => (
            <Marker
              key={`tanker-${s.id}`}
              position={{ lat: s.tanker.lat, lng: s.tanker.lng }}
              icon={ICON_TANKER_ACTIVE}
              title={s.tanker.name}
              onClick={() => setSelected({ kind: 'session', session: s, pos: { lat: s.tanker.lat, lng: s.tanker.lng } })}
            />
          ))}

          {/* Aircraft markers (blue) */}
          {sessions.map(s => (
            <Marker
              key={`ac-${s.id}`}
              position={{ lat: s.aircraft.lat, lng: s.aircraft.lng }}
              icon={ICON_AIRCRAFT}
              title={s.aircraft.reg}
              onClick={() => setSelected({ kind: 'aircraft', session: s, pos: { lat: s.aircraft.lat, lng: s.aircraft.lng } })}
            />
          ))}

          {/* Idle tanker markers (yellow) */}
          {idleTankers.map(t => (
            <Marker
              key={`idle-${t.id}`}
              position={{ lat: t.lat, lng: t.lng }}
              icon={ICON_TANKER_IDLE}
              title={t.name}
              onClick={() => setSelected({ kind: 'idle', tanker: t, pos: { lat: t.lat, lng: t.lng } })}
            />
          ))}

          {/* InfoWindow */}
          {selected && (
            <InfoWindow position={selected.pos} onCloseClick={closeInfo}>
              <div className="lf-infowindow">
                {selected.kind === 'session' && (
                  <>
                    <div className="lf-iw-badge active">&#128308; FUELING</div>
                    <div className="lf-iw-title">{selected.session.tanker.name}</div>
                    <div className="lf-iw-row">⛽ {selected.session.tanker.fuelType}</div>
                    <div className="lf-iw-row">📊 {selected.session.tanker.level}% remaining</div>
                    <hr className="lf-iw-divider" />
                    <div className="lf-iw-row">✈️ Fueling: <strong>{selected.session.aircraft.reg}</strong></div>
                    <div className="lf-iw-row">👤 {selected.session.fueler}</div>
                    <div className="lf-iw-row">💧 {selected.session.dispensed} Gal dispensed</div>
                    <div className="lf-iw-row">⏱ {elapsed(selected.session.startTime)}</div>
                    <div className="lf-iw-row">🔢 Step {selected.session.step}/{selected.session.totalSteps}</div>
                  </>
                )}
                {selected.kind === 'aircraft' && (
                  <>
                    <div className="lf-iw-badge aircraft">&#128309; AIRCRAFT</div>
                    <div className="lf-iw-title">{selected.session.aircraft.reg}</div>
                    <div className="lf-iw-row">{selected.session.aircraft.type}</div>
                    <div className="lf-iw-row">📍 {selected.session.aircraft.position}</div>
                    <hr className="lf-iw-divider" />
                    <div className="lf-iw-row">🚛 Fueled by: <strong>{selected.session.tanker.name}</strong></div>
                    <div className="lf-iw-row">💧 {selected.session.dispensed} Gal so far</div>
                  </>
                )}
                {selected.kind === 'idle' && (
                  <>
                    <div className="lf-iw-badge idle">&#128993; STANDBY</div>
                    <div className="lf-iw-title">{selected.tanker.name}</div>
                    <div className="lf-iw-row">⛽ {selected.tanker.fuelType}</div>
                    <div className="lf-iw-row">📊 {selected.tanker.level}% remaining</div>
                    <div className="lf-iw-row">📍 {selected.tanker.location}</div>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Legend */}
        <div className="lf-map-legend">
          <div className="lf-legend-item"><span className="lf-legend-dot red" />Active tanker</div>
          <div className="lf-legend-item"><span className="lf-legend-dot blue" />Aircraft</div>
          <div className="lf-legend-item"><span className="lf-legend-dot yellow" />Idle tanker</div>
        </div>
      </div>
  );
}

/* ── Session Card ───────────────────────────────────────────────── */
function SessionCard({ session }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const pct = Math.round((session.step / session.totalSteps) * 100);

  return (
    <div className="lf-session-card">
      {/* Header */}
      <div className="lf-session-header">
        <div className="lf-live-badge">
          <PulsingDot /> LIVE
        </div>
        <span className="lf-session-id">{session.id}</span>
        <span className="lf-elapsed">
          <FaClock /> {elapsed(session.startTime)}
        </span>
      </div>

      {/* Main connection row: tanker → aircraft */}
      <div className="lf-connection">
        {/* Tanker */}
        <div className="lf-entity tanker-entity">
          <div className="lf-entity-icon tanker-icon-bg">
            <FaTruck />
          </div>
          <div className="lf-entity-info">
            <div className="lf-entity-name">{session.tanker.name}</div>
            <div className="lf-entity-sub">{session.tanker.fuelType}</div>
            <FuelBar level={session.tanker.level} height={6} />
            <div className="lf-entity-level">{session.tanker.level}% remaining</div>
          </div>
        </div>

        {/* Animated hose line */}
        <div className="lf-hose">
          <div className="lf-hose-line">
            <div className="lf-hose-flow" />
          </div>
          <div className="lf-hose-label">
            <FaGasPump /> {session.dispensed} Gal
          </div>
          <FaArrowRight className="lf-hose-arrow" />
        </div>

        {/* Aircraft */}
        <div className="lf-entity aircraft-entity">
          <div className="lf-entity-icon aircraft-icon-bg">
            <FaPlane />
          </div>
          <div className="lf-entity-info">
            <div className="lf-entity-name">{session.aircraft.reg}</div>
            <div className="lf-entity-sub">{session.aircraft.type}</div>
            <div className="lf-entity-sub">
              <FaMapMarkerAlt style={{ marginRight: 4 }} />
              {session.aircraft.position}
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="lf-progress-section">
        <div className="lf-progress-label">
          <span>Step {session.step} of {session.totalSteps}</span>
          <span>{pct}% complete</span>
        </div>
        <div className="lf-progress-bar-track">
          <div className="lf-progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <StepProgress step={session.step} total={session.totalSteps} />
      </div>

      {/* Fueler */}
      <div className="lf-fueler-row">
        <FaUser className="lf-fueler-icon" />
        <span>{session.fueler}</span>
        <span className="lf-fueler-badge">On site</span>
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────── */
export default function LiveFueling() {
  const [sessions, setSessions] = useState(INITIAL_SESSIONS);
  const [, setTick] = useState(0);

  // Re-render every second for elapsed timers
  useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Simulate step advancement every 30s
  useEffect(() => {
    const t = setInterval(() => {
      setSessions(prev => prev.map(s => ({
        ...s,
        step:      Math.min(s.step + 1, s.totalSteps),
        dispensed: Math.min(s.dispensed + Math.round(Math.random() * 8 + 4), 250),
      })));
    }, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Live Fueling</h1>
          <p className="page-subtitle">Real-time view of active fueling sessions</p>
        </div>
        <div className="lf-header-status">
          <PulsingDot />
          <span>{sessions.length} active session{sessions.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Summary bar */}
      <div className="lf-summary-row">
        <div className="lf-summary-card">
          <FaBolt className="lf-summary-icon active" />
          <div>
            <div className="lf-summary-value">{sessions.length}</div>
            <div className="lf-summary-label">Active sessions</div>
          </div>
        </div>
        <div className="lf-summary-card">
          <FaTruck className="lf-summary-icon idle" />
          <div>
            <div className="lf-summary-value">{IDLE_TANKERS.length}</div>
            <div className="lf-summary-label">Idle tankers</div>
          </div>
        </div>
        <div className="lf-summary-card">
          <FaGasPump className="lf-summary-icon dispensed" />
          <div>
            <div className="lf-summary-value">
              {sessions.reduce((s, x) => s + x.dispensed, 0)} Gal
            </div>
            <div className="lf-summary-label">Dispensed this shift</div>
          </div>
        </div>
        <div className="lf-summary-card">
          <FaCheckCircle className="lf-summary-icon done" />
          <div>
            <div className="lf-summary-value">{RECENT_COMPLETED.length}</div>
            <div className="lf-summary-label">Completed today</div>
          </div>
        </div>
      </div>

      {/* Two-column dashboard: map left, sessions right */}
      <div className="lf-dashboard">
        {/* ── Left column: map + idle tankers ── */}
        <div className="lf-dash-left">
          <ApronMap sessions={sessions} idleTankers={IDLE_TANKERS} />

          {/* Idle tankers */}
          <div className="lf-panel">
            <div className="lf-panel-title"><FaTruck /> Idle Tankers</div>
            {IDLE_TANKERS.map(t => {
              const clr = t.level < 20 ? '#db3131' : t.level < 40 ? '#f5af18' : '#35c068';
              return (
                <div key={t.id} className="lf-idle-row">
                  <div className="lf-idle-icon"><FaTruck /></div>
                  <div className="lf-idle-info">
                    <div className="lf-idle-name">{t.name}</div>
                    <div className="lf-idle-sub">{t.fuelType} · {t.location}</div>
                    <FuelBar level={t.level} height={6} />
                  </div>
                  <div className="lf-idle-level" style={{ color: clr }}>{t.level}%</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right column: active sessions + recently completed ── */}
        <div className="lf-dash-right">
          <div className="lf-section-title">
            <FaBolt /> Active Sessions
            {sessions.length === 0 && <span className="lf-no-sessions">No active sessions right now</span>}
          </div>

          <div className="lf-sessions-list">
            {sessions.map(s => <SessionCard key={s.id} session={s} />)}
          </div>

          {/* Recently completed */}
          <div className="lf-panel">
            <div className="lf-panel-title"><FaCheckCircle /> Recently Completed</div>
            {RECENT_COMPLETED.map(r => (
              <div key={r.id} className="lf-completed-row">
                <FaCheckCircle className="lf-completed-check" />
                <div className="lf-completed-info">
                  <div className="lf-completed-main">
                    <strong>{r.aircraft}</strong> via {r.tanker}
                  </div>
                  <div className="lf-completed-sub">
                    {r.fueler} · {r.amount} · {r.duration} · <span className="lf-time-ago">{r.completedAgo}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
